import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { FluctuationPopup, FluctuationTypeKey, FluctuationType } from '@bingads-webui-campaign-react/fluctuation-popup';
import { ChartDataAdapter } from './chart-data-adapter';
import { FluctuationBulb } from './fluctuation-bulb';
import { instrumentationSchema } from './inline-chart-instr-schema';
import { ImpressionsID, ClicksID, SpentID, ConvID, PartialConvID } from './chart-metrics-adapter';

function getFluctuationType(metricId) {
  switch (metricId) {
    case ImpressionsID:
      return FluctuationType[FluctuationTypeKey.Impression];
    case ClicksID:
      return FluctuationType[FluctuationTypeKey.Click];
    case SpentID:
      return FluctuationType[FluctuationTypeKey.Spend];
    case ConvID:
    case PartialConvID:
      return FluctuationType[FluctuationTypeKey.Conv];
    default:
      return null;
  }
}

export class FluctuationPanel extends React.PureComponent {
  constructor(props) {
    super(props);

    this.initializeInstrumentation();
    this.chartDataAdapter = new ChartDataAdapter({
      i18n: this.props.deps.i18n,
      permissions: this.props.deps.context.Permissions,
    });
    this.bulbRefArr = [];

    this.state = {
      chart: null,
      openedFlucId: null,
    };
  }

  componentDidUpdate() {
    let finalStartAvail = true;
    let finalEndAvail = true;
    this.bulbRefArr.forEach((item) => {
      if (item.bulbElem.current) {
        const { isStartAvail, isEndAvail } =
          this.hideChartLabelIfApplicable(item.bulbElem.current);
        finalStartAvail = finalStartAvail && isStartAvail;
        finalEndAvail = finalEndAvail && isEndAvail;
      }
    });
    if (this.props.chartLabelStatus.isShowStartDateLabel !== finalStartAvail ||
      this.props.chartLabelStatus.isShowEndDateLabel !== finalEndAvail) {
      this.props.onChartLabelToggle({
        isShowStartDateLabel: finalStartAvail,
        isShowEndDateLabel: finalEndAvail,
      });
    }
  }

  onChartUpdated = (chart) => {
    this.setState({
      chart,
    });
  };

  initializeInstrumentation = () => {
    this.props.deps.instrumentation.addScenario(instrumentationSchema);

    const inlineChartScenario = this.props.deps.instrumentation.inlineChart.create();
    this.activities = {
      chartActionActivity: inlineChartScenario.inlineChartAction.create(),
      chartErrorActivity: inlineChartScenario.inlineChartError.create(),
    };
  };

  isTwoRectOverlapping = (elem1, elem2) =>
  // Check Leftoverlap, element 1 in element 2, element 2 inside element 1, right overlap
    ((elem1.left >= elem2.left && elem2.right >= elem1.left) ||
      (elem1.left >= elem2.left && elem1.right <= elem2.right) ||
      (elem2.left >= elem1.left && elem2.right <= elem1.right) ||
      (elem1.right >= elem2.left && elem2.right >= elem1.right));

  hideChartLabelIfApplicable = (bulbElem) => {
    const startLabelRef = this.props.chartLabelRef.current.startLabelRef.current;
    const endLabelRef = this.props.chartLabelRef.current.endLabelRef.current;
    const bulbClientRect = bulbElem.getBoundingClientRect();
    let isBulbOverlappingWithStart = false;
    let isBulbOverlappingWithEnd = false;

    if (startLabelRef) {
      const startDateRect = startLabelRef.getBoundingClientRect();
      isBulbOverlappingWithStart = this.isTwoRectOverlapping(startDateRect, bulbClientRect);
    }
    if (endLabelRef) {
      const lastDateRect = endLabelRef.getBoundingClientRect();
      isBulbOverlappingWithEnd = this.isTwoRectOverlapping(lastDateRect, bulbClientRect);
    }

    return {
      isStartAvail: !isBulbOverlappingWithStart,
      isEndAvail: !isBulbOverlappingWithEnd,
    };
  };

  logContext = (date, fluctuations) => {
    fluctuations.forEach((fluc) => {
      const logObject = {
        fluxId: fluc.UniqueId,
        PrimaryMetricId: this.props.primaryMetricId,
        SecondaryMetricId: this.props.secondaryMetricId,
        eventType: 'FluxShown',
        fluxContent: {
          actions: _.chain(fluc.Actions)
            .pluck('Message')
            .flatten().pluck('MessageId')
            .value(),
          rootCauses: _.chain(fluc.RootCauses)
            .pluck('Message')
            .flatten().pluck('MessageId')
            .value(),
          description: _.pluck(fluc.Description, 'MessageId'),
        },
        fluxDate: date,
        fluxKpi: fluc.FluctuationType,
        userId: this.props.deps.context.CurrentUser.Id,
        isInternal: this.props.deps.context.CurrentUser.IsInternal,
        customerId: fluc.CustomerId,
        accountId: fluc.AccountId,
        campaignId: fluc.CampaignId,
      };
      this.activities.chartActionActivity.trace(`Bulb Rendered for ${JSON.stringify(logObject)}`, 'FluctuationAction');
    });
  };

  render() {
    if (!this.state.chart || !this.props.fluctuations || this.props.fluctuations.length === 0
      || !this.state.chart.series) {
      return null;
    }
    this.bulbRefArr = [];
    const flucPlaceholders = [];
    this.props.fluctuations.forEach((fluc) => {
      if (fluc.FluctuationsPerDate.length === 0) {
        return;
      }
      this.state.chart.series[0].data.forEach((point, idx) => {
        if (point.name === fluc.formattedDate) {
          if (this.activities) {
            this.logContext(fluc.Date, fluc.FluctuationsPerDate);
          }

          // substract half bulb width from x to align the bulb center with chart points
          const bulbWidth = 28;
          const clientX = this.state.chart.series[0].points[idx].clientX +
            (this.state.chart.plotLeft - (bulbWidth / 2));
          const isRCUnknown = !_.isUndefined(_.find(
            fluc.FluctuationsPerDate,
            item =>
              item.RootCauses &&
              item.RootCauses[0] &&
              item.RootCauses[0].Message &&
              item.RootCauses[0].Message.MessageId === 'RCUnknown'
          ));

          const bulbElem = (
            <FluctuationBulb
              id={fluc.FluctuationsPerDate[0].UniqueId}
              ref={(ref) => {
                if (ref) {
                  this.bulbRefArr.push(ref);
                }
              }}
              isRed={isRCUnknown}
              isYellow
              left={clientX}
              imageSrc={`${this.props.deps.imagePath}fluctuation-bulb.png`}
            />
          );

          // there could be four series when moving range is checked
          // or two series if moving range is not checked
          // primary, (primaryComparison), secondary, (secondaryComparison)
          // send all available ones over for vNext
          // do not send comparison (moving average) to current UI
          const metrics = [];
          this.state.chart.series.forEach((series) => {
            const metricId = (series.userOptions.id.indexOf('primary') !== -1) ?
              this.props.primaryMetricId :
              this.props.secondaryMetricId;

            if (metricId !== -1 &&
              series.data.length > 0) {
              metrics.push({
                name: series.name,
                value: this.chartDataAdapter.formatMetric(
                  metricId,
                  series.data[idx].y
                ).toString(),
                line: series.userOptions.id,
                color: series.userOptions.color,
              });
            }
          });

          const fluctuationTypeIds = _.compact([
            getFluctuationType(this.props.primaryMetricId),
            getFluctuationType(this.props.secondaryMetricId),
          ]);

          // this.activities.chartActionActivity.trace('Fluctuation data populated on chart',
          // 'InlineChartAction');
          const isOpen = !_.isUndefined(_.find(fluc.FluctuationsPerDate, item =>
            item.UniqueId === this.state.openedFlucId));

          flucPlaceholders.push((
            <div className="fluctuation-placeholder" key={fluc.FluctuationsPerDate[0].UniqueId}>
              <FluctuationPopup
                withoutOpporCheck={fluc.withoutOpporCheck}
                updateFlucData={this.props.updateFlucData}
                updateLoadingStatus={this.props.updateLoadingStatus}
                asyncLoadFluctuationConfig={{
                  dateRange: this.props.dateRange,
                  fluctuationTypeIds,
                }}
                fluctuation={fluc.withoutOpporCheck ? [] : fluc}
                onFlucOpen={(metricId, flucId) => {
                  this.setState({
                    openedFlucId: flucId,
                  });
                  this.flucPage = _.findIndex(fluc.FluctuationsPerDate, {
                    UniqueId: flucId,
                  });
                  if (metricId !== this.props.primaryMetricId &&
                    metricId !== this.props.secondaryMetricId) {
                    this.props.onPrimaryMetricChange({
                      id: metricId,
                    });
                  }
                }}
                deps={{
                  i18n: this.props.deps.i18n,
                  context: this.props.deps.context,
                  instrumentation: this.props.deps.instrumentation,
                  token: this.props.deps.token,
                  imagePath: this.props.deps.imagePath,
                }}
                metrics={metrics}
                triggerButton={bulbElem}
                isOpen={isOpen}
                onFlucDismiss={this.props.onFlucDismiss}
                page={isOpen ? this.flucPage : 0}
              />
            </div>
          ));
        }
      });
    });
    return (
      <div>
        {flucPlaceholders}
      </div>
    );
  }
}

FluctuationPanel.propTypes = {
  fluctuations: PropTypes.arrayOf(PropTypes.shape({
    Date: PropTypes.string.isRequired,
    formattedDate: PropTypes.string.isRequired,
    FluctuationsPerDate: PropTypes.arrayOf(PropTypes.shape({
      UniqueId: PropTypes.string.isRequired,
    })),
  })).isRequired,
  onFlucDismiss: PropTypes.func.isRequired,
  primaryMetricId: PropTypes.number.isRequired,
  secondaryMetricId: PropTypes.number.isRequired,
  chartLabelStatus: PropTypes.shape({
    isShowStartDateLabel: PropTypes.bool,
    isShowEndDateLabel: PropTypes.bool,
  }).isRequired,
  onChartLabelToggle: PropTypes.func.isRequired,
  onPrimaryMetricChange: PropTypes.func.isRequired,
  chartLabelRef: PropTypes.shape({
    current: PropTypes.shape({
      startLabelRef: PropTypes.shape({
        current: PropTypes.node,
      }),
      endLabelRef: PropTypes.shape({
        current: PropTypes.node,
      }),
    }),
  }).isRequired,
  deps: PropTypes.shape({
    token: PropTypes.string,
    imagePath: PropTypes.string,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
      formatDate: PropTypes.func,
    }).isRequired,
    instrumentation: PropTypes.shape({
      addScenario: PropTypes.func,
      inlineChart: PropTypes.shape({
        create: PropTypes.func,
      }),
      fluctuation: PropTypes.shape({
        create: PropTypes.func,
      }),
    }),
    context: PropTypes.shape({
      CurrentCustomer: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentAccount: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentCampaign: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentUser: PropTypes.shape({
        Id: PropTypes.number,
        IsInternal: PropTypes.bool,
      }),
      Permissions: PropTypes.shape({
        IsABFluctuationBlubYellow: PropTypes.bool,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  dateRange: PropTypes.shape({}).isRequired,
  updateFlucData: PropTypes.func.isRequired,
  updateLoadingStatus: PropTypes.func.isRequired,
};
